import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';
import { getLanguage } from '@deadpan/shared';

export const BombRelayPlay: React.FC = () => {
  const { socket, currentRoom, gameState, myPlayerId } = useSocket();
  const t = useTranslations();
  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, []);

  if (!currentRoom || !gameState) {
    return null;
  }

  const orderIds = gameState.bombRelayOrder || [];
  const orderPlayers = orderIds
    .map((playerId) => currentRoom.players.find((player) => player.id === playerId))
    .filter((player): player is NonNullable<typeof player> => Boolean(player));

  const currentGuesserId = gameState.bombRelayCurrentGuesserId;
  const currentDescriberId = gameState.bombRelayCurrentDescriberId;

  const isMyTurnDescriber = myPlayerId === currentDescriberId;
  const canSeeWord = myPlayerId !== currentGuesserId;
  const lang = getLanguage();
  const localizedWord = lang === 'zh'
    ? (gameState.bombRelayCurrentWordZh || gameState.bombRelayCurrentWord || '')
    : (gameState.bombRelayCurrentWord || gameState.bombRelayCurrentWordZh || '');
  const wordForMe = canSeeWord ? localizedWord : '???';

  const timeRemaining = gameState.timer
    ? Math.max(0, Math.ceil((gameState.timer.timerEndTime - now) / 1000))
    : 0;

  const handleGuessCorrect = () => {
    if (!socket || !isMyTurnDescriber) return;
    socket.emit('client:bomb-relay-guess-correct');
  };

  const getSeatPositions = (count: number): Array<{ x: number; y: number }> => {
    // Coordinates are in percentages and ordered clockwise.
    switch (count) {
      case 2:
        return [
          { x: 30, y: 28 }, // top-left
          { x: 70, y: 28 }, // top-right
        ];
      case 3:
        return [
          { x: 30, y: 24 }, // top-left
          { x: 70, y: 24 }, // top-right
          { x: 50, y: 74 }, // bottom-center
        ];
      case 4:
        return [
          { x: 30, y: 24 }, // top-left
          { x: 70, y: 24 }, // top-right
          { x: 70, y: 74 }, // bottom-right
          { x: 30, y: 74 }, // bottom-left
        ];
      case 5:
        return [
          { x: 34, y: 18 }, // top-left
          { x: 66, y: 18 }, // top-right
          { x: 82, y: 52 }, // middle-right
          { x: 50, y: 84 }, // bottom-center
          { x: 18, y: 52 }, // middle-left
        ];
      case 6:
        return [
          { x: 30, y: 18 }, // top-left
          { x: 70, y: 18 }, // top-right
          { x: 82, y: 50 }, // middle-right
          { x: 70, y: 82 }, // bottom-right
          { x: 30, y: 82 }, // bottom-left
          { x: 18, y: 50 }, // middle-left
        ];
      case 7:
        return [
          { x: 34, y: 14 }, // top-left
          { x: 66, y: 14 }, // top-right
          { x: 84, y: 34 }, // upper-right
          { x: 84, y: 66 }, // lower-right
          { x: 50, y: 86 }, // bottom-center
          { x: 16, y: 66 }, // lower-left
          { x: 16, y: 34 }, // upper-left
        ];
      case 8:
        return [
          { x: 34, y: 12 }, // top-left
          { x: 66, y: 12 }, // top-right
          { x: 82, y: 30 }, // upper-right
          { x: 82, y: 54 }, // middle-right
          { x: 66, y: 82 }, // lower-right
          { x: 34, y: 82 }, // lower-left
          { x: 18, y: 54 }, // middle-left
          { x: 18, y: 30 }, // upper-left
        ];
      default:
        return [];
    }
  };

  const seatPositions = getSeatPositions(orderPlayers.length);

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <div className="w-full text-center mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent mb-2">
          {t.games.bombRelay.title}
        </h2>
        <p className="text-gray-600">{t.games.bombRelay.countdownHint}</p>
      </div>

      <div className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl p-4 mb-4 shadow-lg text-center">
        <div className="text-sm uppercase tracking-wide opacity-90">{t.games.bombRelay.countdownLabel}</div>
        <div className="text-4xl sm:text-5xl font-bold">{timeRemaining}</div>
      </div>

      <div className="w-full rounded-xl border-2 border-indigo-200 bg-indigo-50 p-4 mb-6">
        <div className="text-sm text-indigo-700 font-semibold mb-2">{t.games.bombRelay.currentWord}</div>
        <div className="w-full overflow-x-auto">
          <div className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-indigo-900 text-center whitespace-nowrap">{wordForMe || '...'}</div>
        </div>
        {gameState.bombRelayCurrentWordLevel && (
          <p className="text-center text-xs text-indigo-700 mt-1">
            {t.games.bombRelay.difficultyPrefix}{gameState.bombRelayCurrentWordLevel}
          </p>
        )}
      </div>

      <div className="w-full mb-6 rounded-2xl bg-gray-50/70 border border-gray-200 p-4 sm:p-6 relative h-[420px] sm:h-[500px]">
        {orderPlayers.map((player, index) => {
          const pos = seatPositions[index] || { x: 50, y: 50 };
          const isGuesser = player.id === currentGuesserId;
          const isDescriber = player.id === currentDescriberId;
          const isMe = player.id === myPlayerId;
          const roleLabel = isGuesser
            ? t.games.bombRelay.guesserLabel
            : isDescriber
            ? t.games.bombRelay.describerLabel
            : t.games.bombRelay.observerLabel;

          const roleStyle = isGuesser
            ? 'border-red-400 bg-red-50 text-red-800'
            : isDescriber
            ? 'border-blue-400 bg-blue-50 text-blue-800'
            : 'border-gray-300 bg-white text-gray-700';

          return (
            <div
              key={player.id}
              style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
              className={`absolute -translate-x-1/2 -translate-y-1/2 w-[128px] sm:w-[142px] rounded-xl border-2 px-2 py-2 sm:px-3 sm:py-2 shadow-sm ${roleStyle}`}
            >
              <div className="text-[11px] sm:text-xs font-semibold text-center truncate">
                {roleLabel}
              </div>
              <div className="mt-1 text-sm sm:text-base font-bold text-center truncate">
                {player.name}{isMe ? t.games.bombRelay.youLabel : ''}
              </div>
              <div className="mt-1 text-center text-lg leading-none" aria-label={isGuesser ? 'bomb' : 'not-bomb'}>
                {isGuesser ? '💣' : '·'}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={handleGuessCorrect}
        disabled={!isMyTurnDescriber}
        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 sm:py-4 px-4 rounded-xl font-bold text-base sm:text-lg hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all"
      >
        {isMyTurnDescriber ? t.games.bombRelay.guessCorrectButton : t.games.bombRelay.waitDescriberButton}
      </button>
    </div>
  );
};

